import { API_BASE } from "../config";
import React, { useState, useEffect } from 'react';
import { printFileContent, printProgramDetail } from '../utils/printHelper';
import { authedBlobUrl, revokeBlobUrl } from '../utils/authedBlobUrl';
import XaiPromptPanel from './XaiPromptPanel';
import { CitationsButton } from './Citations';
import { citationCount } from '../utils/xaiHelpers';

const FitnessProgramManagement = ({ fetchWithAuth, fetchWithAuthFormData, onOpenAIAgent, refreshKey, token }) => {
  const [programs, setPrograms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedProgramDetails, setSelectedProgramDetails] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewingProgram, setFileViewingProgram] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedVideoExercise, setSelectedVideoExercise] = useState(null);
  const [xaiProgramId, setXaiProgramId] = useState(null);
  const [xaiPromptData, setXaiPromptData] = useState(null);

  const [formData, setFormData] = useState({
    program_name: '',
    program_description: '',
    primary_goal: 'general_fitness',
    secondary_goals: [],
    fitness_level: 'intermediate',
    target_body_areas: [],
    program_duration_weeks: 8,
    sessions_per_week: 4,
    strength_sessions_per_week: 3,
    cardio_sessions_per_week: 2,
    flexibility_sessions_per_week: 1,
    rest_days_per_week: 1,
    is_active: true,
    start_date: new Date().toISOString().split('T')[0],
    end_date: null
  });

  useEffect(() => {
    loadPrograms();
  }, [refreshKey]);

  useEffect(() => {
    // Calculate end date when start date or duration changes
    if (formData.start_date && formData.program_duration_weeks) {
      const start = new Date(formData.start_date);
      const end = new Date(start);
      end.setDate(end.getDate() + (formData.program_duration_weeks * 7));
      setFormData(prev => ({ ...prev, end_date: end.toISOString().split('T')[0] }));
    }
  }, [formData.start_date, formData.program_duration_weeks]);

  const loadPrograms = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/fitness-programs/list?include_inactive=true`);
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      console.error('Failed to load fitness programs:', error);
    }
  };

  const loadProgramDetails = async (programId) => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/fitness-programs/${programId}`);
      if (response.ok) {
        const details = await response.json();
        setSelectedProgramDetails(details);
        setShowDetailView(true);
      }
    } catch (error) {
      console.error('Failed to load program details:', error);
      alert('Unable to load program details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    printProgramDetail(selectedProgramDetails);
  };

  // Map session_details to weekly_schedule days
  const getSessionsForDay = (dayKey, weeklySchedule, sessionDetails) => {
    if (!weeklySchedule || !sessionDetails) return null;
    
    const daySchedule = weeklySchedule[dayKey];
    if (!daySchedule || daySchedule.type === 'rest') return null;
    
    // Find matching session by focus
    const matchingSession = sessionDetails.find(
      session => session.focus === daySchedule.focus
    );
    
    return matchingSession;
  };

  // Day order for display
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayLabels = {
    monday: 'Monday',
    tuesday: 'Tuesday', 
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  // Close the file viewer AND release the blob URL. A blob: URL pins its bytes for the lifetime of
  // the document unless revoked, so every viewer-close path must go through here — otherwise each
  // file opened leaks until the tab is closed. (H7, 2026-08-03.)
  const closeFileViewer = () => {
    setFileContent(prev => {
      if (typeof prev === 'string' && prev.startsWith('__PDF__')) {
        revokeBlobUrl(prev.replace('__PDF__', ''));
      }
      return '';
    });
    setShowFileViewer(false);
    setFileViewingProgram(null);
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData({
      program_name: program.program_name || '',
      program_description: program.program_description || '',
      primary_goal: program.primary_goal || 'general_fitness',
      secondary_goals: program.secondary_goals || [],
      fitness_level: program.fitness_level || 'intermediate',
      target_body_areas: program.target_body_areas || [],
      program_duration_weeks: program.program_duration_weeks || 8,
      sessions_per_week: program.sessions_per_week || 4,
      strength_sessions_per_week: program.strength_sessions_per_week || 3,
      cardio_sessions_per_week: program.cardio_sessions_per_week || 2,
      flexibility_sessions_per_week: program.flexibility_sessions_per_week || 1,
      rest_days_per_week: program.rest_days_per_week || 1,
      is_active: program.is_active,
      start_date: program.start_date || new Date().toISOString().split('T')[0],
      end_date: program.end_date
    });
    setShowForm(true);
  };

  const handleDelete = async (programId) => {
    if (!confirm('Are you sure you want to delete this fitness program?')) return;
    
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/fitness-programs/${programId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        loadPrograms();
      } else {
        alert('Failed to delete fitness program');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete fitness program');
    }
  };

  const handleActivate = async (programId) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/fitness-programs/${programId}/activate`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        loadPrograms();
      }
    } catch (error) {
      console.error('Activation error:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.program_name.trim()) {
      alert('Please enter a program name');
      return;
    }

    // Validate session distribution
    const totalSessions = formData.strength_sessions_per_week + 
                          formData.cardio_sessions_per_week + 
                          formData.flexibility_sessions_per_week + 
                          formData.rest_days_per_week;
    if (totalSessions !== 7) {
      alert(`Weekly sessions must total 7 days (currently ${totalSessions})`);
      return;
    }

    try {
      // STEP 1: Create/update program
      const payload = {
        ...formData,
        source_type: uploadedFile ? 'file_upload' : 'manual'
      };

      const url = editingProgram 
        ? `${API_BASE}/api/fitness-programs/${editingProgram.id}`
        : `${API_BASE}/api/fitness-programs/create`;
      
      const method = editingProgram ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save fitness program');
      }

      const savedProgram = await response.json();

      // STEP 2: If there's a file, upload it separately - EXACT COPY FROM MEAL PLAN
      if (uploadedFile) {
        const uploadResponse = await fetchWithAuth(
          `${API_BASE}/api/fitness-programs/${savedProgram.program_id}/upload-file`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(uploadedFile)
          }
        );

        if (!uploadResponse.ok) {
          console.error('File upload failed but program was created');
        }
      }

      // STEP 3: Reload and close
      loadPrograms();
      closeForm();
      
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.message || 'Failed to save fitness program');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProgram(null);
    setShowAdvanced(false);
    setUploadedFile(null);
    setDragActive(false);
    setFormData({
      program_name: '',
      program_description: '',
      primary_goal: 'general_fitness',
      secondary_goals: [],
      fitness_level: 'intermediate',
      target_body_areas: [],
      program_duration_weeks: 8,
      sessions_per_week: 4,
      strength_sessions_per_week: 3,
      cardio_sessions_per_week: 2,
      flexibility_sessions_per_week: 1,
      rest_days_per_week: 1,
      is_active: true,
      start_date: new Date().toISOString().split('T')[0],
      end_date: null
    });
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    const validTypes = [
      'application/pdf',
      'text/plain',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    
    const validExtensions = /\.(pdf|txt|jpg|jpeg|png)$/i;
    
    if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
      alert('Please upload a valid file (PDF, TXT, or JPG/PNG)');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        setUploadedFile({
          name: file.name,
          type: file.type,
          size: file.size,
          content: content
        });
      } catch (error) {
        console.error('Error reading file:', error);
        alert('Failed to read file. Please try again.');
      }
    };
    
    reader.readAsDataURL(file);
  };

  const toggleSecondaryGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      secondary_goals: prev.secondary_goals.includes(goal)
        ? prev.secondary_goals.filter(g => g !== goal)
        : [...prev.secondary_goals, goal]
    }));
  };

  const toggleBodyArea = (area) => {
    setFormData(prev => ({
      ...prev,
      target_body_areas: prev.target_body_areas.includes(area)
        ? prev.target_body_areas.filter(a => a !== area)
        : [...prev.target_body_areas, area]
    }));
  };

  const goalOptions = [
    { value: 'weight_loss', label: 'Weight Loss' },
    { value: 'muscle_gain', label: 'Muscle Gain' },
    { value: 'endurance', label: 'Endurance' },
    { value: 'general_fitness', label: 'General Fitness' },
    { value: 'athletic_performance', label: 'Athletic Performance' },
    { value: 'rehabilitation', label: 'Rehabilitation' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'body_recomposition', label: 'Body Recomposition' }
  ];

  const bodyAreaOptions = [
    'chest', 'back', 'shoulders', 'biceps', 'triceps', 
    'core', 'glutes', 'quadriceps', 'hamstrings', 'calves', 'full_body'
  ];

  const getGoalIcon = (goal) => {
    const option = goalOptions.find(g => g.value === goal);
    return '';
  };

  const getSourceBadge = (sourceType) => {
    const badges = {
      'manual': { bg: 'rgba(30, 58, 95, 0.08)', text: '#1E3A5F', label: 'Manual' },
      'ai_generated': { bg: '#D1FAE5', text: '#065F46', label: 'AI Generated' },
      'file_upload': { bg: '#FEF3C7', text: '#92400E', label: 'From File' },
      'template': { bg: 'rgba(30, 58, 95, 0.08)', text: '#1E3A5F', label: 'Template' },
      'copied': { bg: '#F3F4F6', text: '#4B5563', label: 'Copied' }
    };
    return badges[sourceType] || badges['manual'];
  };

  const totalSessions = formData.strength_sessions_per_week + 
                        formData.cardio_sessions_per_week + 
                        formData.flexibility_sessions_per_week + 
                        formData.rest_days_per_week;

  return (
    <div>
      {/* Header */}
      <div className="fpm-header">
        <div>
          <h3 className="fpm-title">
            Fitness Programs
          </h3>
          <p className="fpm-subtitle">
            {programs.length} program{programs.length !== 1 ? 's' : ''} • Manage your workout plans
          </p>
        </div>
        <div className="fpm-headerActions">
          <button
            onClick={() => {
              setEditingProgram(null);
              setShowForm(true);
            }}
            className="fpm-createBtn"
          >
            <span>+</span> Create Manual
          </button>
          {onOpenAIAgent && (
            <button
              onClick={() => onOpenAIAgent('fitness-program')}
              className="fpm-aiGenerateBtn"
            >
              AI Generate
            </button>
          )}
        </div>
      </div>

      {/* Programs List */}
      {programs.length === 0 ? (
        <div className="fpm-emptyState">
          <div className="fpm-emptyIcon"></div>
          <h4 className="fpm-emptyTitle">No Fitness Programs Yet</h4>
          <p className="fpm-emptyText">
            Use the buttons above to create your first program
          </p>
        </div>
      ) : (
        <div className="fpm-programList">
          {programs.map(program => {
            const sourceBadge = getSourceBadge(program.source_type);
            return (
              <div
                key={program.id}
                className="fpm-programCard" style={{ border: `2px solid ${program.is_active ? 'var(--brand-primary)' : '#E5E7EB'}` }}
                onClick={() => loadProgramDetails(program.id)}
              >
                <div className="fpm-cardLayout">
                  <div className="fpm-cardContent">
                    <div className="fpm-cardTitleRow">
                      <span className="fpm-goalIcon">{getGoalIcon(program.primary_goal)}</span>
                      <h4 className="fpm-programName">
                        {program.program_name}
                      </h4>
                      {program.is_active && (
                        <span className="fpm-activeBadge">
                          ACTIVE
                        </span>
                      )}
                      <span className="fpm-sourceBadge" style={{
                        backgroundColor: sourceBadge.bg,
                        color: sourceBadge.text
                      }}>
                        {sourceBadge.label}
                      </span>
                    </div>
                    <p className="fpm-programMeta">
                      {program.program_duration_weeks} weeks • {program.sessions_per_week} sessions/week • {program.fitness_level}
                    </p>
                    {program.program_duration_weeks > 0 && (
                      <div className="fpm-progressBarWrapper">
                        <div className="fpm-progressBarTrack">
                          <div
                            className="fpm-progressBarFill"
                            style={{ width: `${Math.min(100, Math.round(((program.current_week || 1) / program.program_duration_weeks) * 100))}%` }}
                          />
                        </div>
                        <span className="fpm-progressLabel">
                          Week {program.current_week || 1} of {program.program_duration_weeks}
                          {' '}({Math.min(100, Math.round(((program.current_week || 1) / program.program_duration_weeks) * 100))}%)
                        </span>
                      </div>
                    )}
                    {program.program_description && (
                      <p className="fpm-programDescription">
                        {program.program_description.substring(0, 120)}
                        {program.program_description.length > 120 ? '...' : ''}
                      </p>
                    )}
                  </div>

                  <div className="fpm-cardActions" onClick={(e) => e.stopPropagation()}>
                    {!(program.attached_file_path && program.source_type === 'manual') && (
                      <button
                        onClick={() => loadProgramDetails(program.id)}
                        className="fpm-viewBtn"
                      >
                        View
                      </button>
                    )}
                    {!program.is_active && (
                      <button
                        onClick={() => handleActivate(program.id)}
                        className="fpm-activateBtn"
                      >
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(program)}
                      className="fpm-editBtn"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(program.id)}
                      className="fpm-deleteBtn"
                    >
                      Delete
                    </button>
                  </div>
                  {program.attached_file_path && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setFileViewingProgram(program);
                        const fileName = program.upload_filename || '';
                        const isPdf = fileName.toLowerCase().endsWith('.pdf');
                        if (isPdf) {
                          // H7: fetch with the Authorization header and render from a blob: URL —
                          // the token must never appear in a URL. The __PDF__ sentinel and the
                          // iframe below are unchanged; only the source of the URL differs.
                          setFileContent('Loading...');
                          setShowFileViewer(true);
                          try {
                            const blobUrl = await authedBlobUrl(
                              fetchWithAuth,
                              `${API_BASE}/api/fitness-programs/${program.id}/view-file`
                            );
                            setFileContent(`__PDF__${blobUrl}`);
                          } catch (err) {
                            console.error('Failed to load fitness program file:', err);
                            setFileContent('Failed to load file content');
                          }
                        } else {
                          setFileContent('Loading...');
                          setShowFileViewer(true);
                          try {
                            const response = await fetchWithAuth(
                              `${API_BASE}/api/fitness-programs/${program.id}/view-file`
                            );
                            if (response.ok) {
                              const text = await response.text();
                              setFileContent(text);
                            } else {
                              setFileContent('Failed to load file content');
                            }
                          } catch (err) {
                            setFileContent('Error loading file: ' + err.message);
                          }
                        }
                      }}
                      className="fpm-fileBtn"
                    >
                      View {program.upload_filename}
                    </button>
                  )}
                  {/* Apple 1.4.1 (2026-10-17): the standard Citations control (same toggle state). */}
                  {program.source_type === 'ai_generated' && (
                    <CitationsButton
                      open={xaiProgramId === program.id}
                      count={citationCount(program.generation_context?.prompt_data || null, 'Fitness Program Generator')}
                      onToggle={() => {
                        if (xaiProgramId === program.id) { setXaiProgramId(null); setXaiPromptData(null); }
                        else {
                          setXaiProgramId(program.id);
                          setXaiPromptData(program.generation_context?.prompt_data || null);
                        }
                      }}
                    />
                  )}
                </div>
                {xaiProgramId === program.id && xaiPromptData && (
                  <XaiPromptPanel promptData={xaiPromptData} featureName="Fitness Program Generator" />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fpm-formOverlay">
          <div className="fpm-formModal">
            {/* Modal Header */}
            <div className="fpm-formHeader">
              <h3 className="fpm-formTitle">
                {editingProgram ? 'Edit Fitness Program' : 'Create Fitness Program'}
              </h3>
              <button
                onClick={closeForm}
                className="fpm-formCloseBtn"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="fpm-formBody">
              {/* Program Name */}
              <div className="fpm-nameGroup">
                <label className="fpm-nameLabel">
                  Program Name *
                </label>
                <input
                  type="text"
                  value={formData.program_name}
                  onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                  placeholder="e.g., 8-Week Strength Builder"
                  className="fpm-nameInput"
                />
              </div>

              {/* Description */}
              <div className="fpm-descGroup">
                <label className="fpm-descLabel">
                  Description
                </label>
                <textarea
                  value={formData.program_description}
                  onChange={(e) => setFormData({ ...formData, program_description: e.target.value })}
                  placeholder="Describe your program goals and approach..."
                  rows={3}
                  className="fpm-descTextarea"
                />
              </div>

              {/* Primary Goal & Fitness Level */}
              <div className="fpm-goalLevelGrid">
                <div>
                  <label className="fpm-goalLabel">
                    Primary Goal
                  </label>
                  <select
                    value={formData.primary_goal}
                    onChange={(e) => setFormData({ ...formData, primary_goal: e.target.value })}
                    className="fpm-goalSelect"
                  >
                    {goalOptions.map(g => (
                      <option key={g.value} value={g.value}>{g.icon} {g.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="fpm-levelLabel">
                    Fitness Level
                  </label>
                  <select
                    value={formData.fitness_level}
                    onChange={(e) => setFormData({ ...formData, fitness_level: e.target.value })}
                    className="fpm-levelSelect"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>

              {/* Duration & Sessions */}
              <div className="fpm-durationGrid">
                <div>
                  <label className="fpm-durationLabel">
                    Duration (weeks)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={formData.program_duration_weeks}
                    onChange={(e) => setFormData({ ...formData, program_duration_weeks: parseInt(e.target.value) || 8 })}
                    className="fpm-durationInput"
                  />
                </div>
                <div>
                  <label className="fpm-sessionsLabel">
                    Sessions/Week
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="7"
                    value={formData.sessions_per_week}
                    onChange={(e) => setFormData({ ...formData, sessions_per_week: parseInt(e.target.value) || 4 })}
                    className="fpm-sessionsInput"
                  />
                </div>
                <div>
                  <label className="fpm-startDateLabel">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="fpm-startDateInput"
                  />
                </div>
              </div>

              {/* Session Distribution */}
              <div className="fpm-distributionGroup">
                <label className="fpm-distributionLabel">
                  Weekly Session Distribution (must equal 7 days)
                </label>
                <div className="fpm-distributionGrid">
                  <div>
                    <label className="fpm-strengthLabel">
                      Strength
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="7"
                      value={formData.strength_sessions_per_week}
                      onChange={(e) => setFormData({ ...formData, strength_sessions_per_week: parseInt(e.target.value) || 0 })}
                      className="fpm-strengthInput"
                    />
                  </div>
                  <div>
                    <label className="fpm-cardioLabel">
                      Cardio
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="7"
                      value={formData.cardio_sessions_per_week}
                      onChange={(e) => setFormData({ ...formData, cardio_sessions_per_week: parseInt(e.target.value) || 0 })}
                      className="fpm-cardioInput"
                    />
                  </div>
                  <div>
                    <label className="fpm-flexLabel">
                      Flexibility
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="7"
                      value={formData.flexibility_sessions_per_week}
                      onChange={(e) => setFormData({ ...formData, flexibility_sessions_per_week: parseInt(e.target.value) || 0 })}
                      className="fpm-flexInput"
                    />
                  </div>
                  <div>
                    <label className="fpm-restLabel">
                      Rest
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="7"
                      value={formData.rest_days_per_week}
                      onChange={(e) => setFormData({ ...formData, rest_days_per_week: parseInt(e.target.value) || 0 })}
                      className="fpm-restInput"
                    />
                  </div>
                </div>
                <p className="fpm-distributionTotal" style={{ color: totalSessions === 7 ? '#059669' : '#DC2626' }}>
                  Total: {totalSessions} days {totalSessions === 7 ? '✓' : '(must be 7)'}
                </p>
              </div>

              {/* File Upload Section */}
              <div className="fpm-uploadGroup">
                <label className="fpm-uploadLabel">
                  Attach Program File (Optional)
                </label>
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className="fpm-dropZone" style={{ border: `2px dashed ${dragActive ? '#1E3A5F' : '#D1D5DB'}`, backgroundColor: dragActive ? '#EBF0F7' : '#F9FAFB' }}
                  onClick={() => document.getElementById('file-upload').click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    accept=".pdf,.txt,.jpg,.jpeg,.png"
                    onChange={handleFileInput}
                    className="fpm-fileInput"
                  />
                  {uploadedFile ? (
                    <div>
                      <div className="fpm-uploadedIcon"></div>
                      <p className="fpm-uploadedName">
                        {uploadedFile.name}
                      </p>
                      <p className="fpm-uploadedSize">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedFile(null);
                        }}
                        className="fpm-removeFileBtn"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="fpm-dropIcon"></div>
                      <p className="fpm-dropText">
                        Drag & drop or click to upload
                      </p>
                      <p className="fpm-dropHint">
                        PDF, TXT, or images (trainer notes, workout sheets, etc.)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Section Toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="fpm-advancedToggle" style={{ marginBottom: showAdvanced ? '20px' : '0' }}
              >
                {showAdvanced ? '▼' : '▶'} Advanced Options
              </button>

              {/* Advanced Options */}
              {showAdvanced && (
                <div className="fpm-advancedSection">
                  {/* Secondary Goals */}
                  <div className="fpm-secondaryGoalsGroup">
                    <label className="fpm-secondaryGoalsLabel">
                      Secondary Goals
                    </label>
                    <div className="fpm-secondaryGoalsGrid">
                      {goalOptions.filter(g => g.value !== formData.primary_goal).map(goal => (
                        <label
                          key={goal.value}
                          className="fpm-goalChip" style={{ border: `2px solid ${formData.secondary_goals.includes(goal.value) ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.secondary_goals.includes(goal.value) ? '#EBF0F7' : 'white' }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.secondary_goals.includes(goal.value)}
                            onChange={() => toggleSecondaryGoal(goal.value)}
                            className="fpm-goalChipCheckbox"
                          />
                          {goal.icon} {goal.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Target Body Areas */}
                  <div>
                    <label className="fpm-bodyAreasLabel">
                      Target Body Areas
                    </label>
                    <div className="fpm-bodyAreasGrid">
                      {bodyAreaOptions.map(area => (
                        <label
                          key={area}
                          className="fpm-bodyAreaChip" style={{ border: `2px solid ${formData.target_body_areas.includes(area) ? '#1E3A5F' : '#E5E7EB'}`, backgroundColor: formData.target_body_areas.includes(area) ? '#EBF0F7' : 'white' }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.target_body_areas.includes(area)}
                            onChange={() => toggleBodyArea(area)}
                            className="fpm-bodyAreaCheckbox"
                          />
                          {area.replace('_', ' ')}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="fpm-formActions">
                <button
                  onClick={closeForm}
                  className="fpm-cancelBtn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!formData.program_name.trim() || totalSessions !== 7}
                  className="fpm-submitBtn" style={{ backgroundColor: (!formData.program_name.trim() || totalSessions !== 7) ? '#D1D5DB' : 'var(--brand-primary)', cursor: (!formData.program_name.trim() || totalSessions !== 7) ? 'not-allowed' : 'pointer' }}
                >
                  {editingProgram ? 'Update Program' : 'Create Program'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail View Modal */}
      {showDetailView && selectedProgramDetails && (
        <div className="fpm-detailOverlay">
          <div className="fpm-detailModal">
            {/* Detail Header */}
            <div className="fpm-detailHeader">
              <div>
                <div className="fpm-detailTitleRow">
                  <span className="fpm-detailGoalIcon">
                    {getGoalIcon(selectedProgramDetails.program?.primary_goal)}
                  </span>
                  <h2 className="fpm-detailTitle">
                    {selectedProgramDetails.program?.program_name}
                  </h2>
                </div>
                <p className="fpm-detailMeta">
                  {selectedProgramDetails.program?.program_duration_weeks} weeks •
                  {selectedProgramDetails.program?.sessions_per_week} sessions/week •
                  {selectedProgramDetails.program?.fitness_level}
                </p>
              </div>
              <div className="fpm-detailActions">

                {/* Print Button */}
                <button
                  onClick={handlePrint}
                  className="fpm-printBtn"
                >
                  Print
                </button>

                <button
                  onClick={() => setShowDetailView(false)}
                  className="fpm-detailCloseBtn"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Detail Content */}
            <div className="fpm-detailBody">
              {/* Description */}
              {selectedProgramDetails.program?.program_description && (
                <div className="fpm-descSection">
                  <h4 className="fpm-descSectionTitle">
                    Description
                  </h4>
                  <p className="fpm-descSectionText">
                    {selectedProgramDetails.program.program_description}
                  </p>
                </div>
              )}

              {/* Weekly Workout Schedule - Organized by Day (hide for manual/file_upload programs) */}
              {selectedProgramDetails.program?.weekly_schedule &&
               selectedProgramDetails.program?.source_type === 'ai_generated' && (
                <div className="fpm-scheduleSection">
                  <h4 className="fpm-scheduleSectionTitle">
                    Weekly Workout Schedule
                  </h4>

                  <div className="fpm-scheduleList">
                    {dayOrder.map(dayKey => {
                      const daySchedule = selectedProgramDetails.program.weekly_schedule[dayKey];
                      const session = getSessionsForDay(
                        dayKey,
                        selectedProgramDetails.program.weekly_schedule,
                        selectedProgramDetails.program.generation_context?.ai_response?.session_details
                      );

                      const isRest = !daySchedule || daySchedule.type === 'rest';

                      return (
                        <div
                          key={dayKey}
                          className="fpm-dayCard"
                        >
                          {/* Day Header */}
                          <div className="fpm-dayHeader" style={{ backgroundColor: isRest ? '#F9FAFB' : (daySchedule?.type === 'cardio' ? '#FEF3C7' : '#EEF2FF'), borderBottom: isRest ? 'none' : '1px solid #E5E7EB' }}>
                            <span className="fpm-dayName" style={{ color: isRest ? '#6B7280' : '#1F2937' }}>
                              {dayLabels[dayKey]}
                            </span>
                            {!isRest && (
                              <span className="fpm-dayTypeBadge">
                                {daySchedule?.type} • {daySchedule?.focus?.replace('_', ' ')} • {daySchedule?.duration_min || 60} min
                              </span>
                            )}
                          </div>

                          {/* Day Content */}
                          {isRest ? (
                            <div className="fpm-restDayText">
                              Rest Day - Recovery & Flexibility
                            </div>
                          ) : session ? (
                            <div className="fpm-dayContent">
                              {/* Warmup */}
                              {session.warmup && session.warmup.length > 0 && (
                                <div className="fpm-warmupSection">
                                  <p className="fpm-warmupLabel">
                                    Warmup
                                  </p>
                                  <p className="fpm-warmupText">
                                    {session.warmup.join(' • ')}
                                  </p>
                                </div>
                              )}

                              {/* Main Workout */}
                              <div className="fpm-workoutSection">
                                <p className="fpm-workoutLabel">
                                  Main Workout
                                </p>
                                <div className="fpm-exerciseList">
                                  {session.main_workout?.map((ex, exIdx) => (
                                    <div
                                      key={exIdx}
                                      className="fpm-exerciseRow"
                                    >
                                      <div className="fpm-exerciseInfo">
                                        <span className="fpm-exerciseName">
                                          {ex.exercise || ex.exercise_name}
                                        </span>
                                        <span className="fpm-exerciseDetail">
                                          {ex.sets && ex.reps ? `${ex.sets} × ${ex.reps}` : ''}
                                          {ex.duration_min ? `${ex.duration_min} min` : ''}
                                          {ex.intervals ? ` (${ex.intervals})` : ''}
                                          {ex.rest_sec ? ` • ${ex.rest_sec}s rest` : ''}
                                        </span>
                                        {/* #93 (second instance, found 2026-10-10) — P-006 c3/4/18
                                            flags any generated exercise that is unsafe for this
                                            user's health context AND computes a goal-equivalent
                                            safe alternative (ai_fitness_generator.py:755). The
                                            verdict reached the browser and NO component read it,
                                            so someone with a shoulder injury was shown an
                                            overhead press with no warning and never saw the
                                            alternative that had already been worked out for them. */}
                                        {ex.contraindication && (
                                          <div className={`fpm-contra fpm-contra--${ex.contraindication.severity || 'caution'}`}
                                               role="note">
                                            <span className="fpm-contraBadge">
                                              {ex.contraindication.severity === 'avoid'
                                                ? '⛔ Not recommended for you'
                                                : '⚠️ Check before doing this'}
                                            </span>
                                            {ex.contraindication.reason && (
                                              <span className="fpm-contraReason">
                                                {ex.contraindication.reason}
                                              </span>
                                            )}
                                            {Array.isArray(ex.contraindication.safe_alternatives)
                                              && ex.contraindication.safe_alternatives.length > 0 && (
                                              <span className="fpm-contraAlt">
                                                Try instead: {ex.contraindication.safe_alternatives
                                                  .map((a) => (typeof a === 'string' ? a : a.name))
                                                  .filter(Boolean).join(' · ')}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                      {ex.video_url && (
                                        <button
                                          onClick={() => setSelectedVideoExercise(ex)}
                                          className="fpm-videoBtn"
                                        >
                                          View Video
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Cooldown */}
                              {session.cooldown && session.cooldown.length > 0 && (
                                <div>
                                  <p className="fpm-cooldownLabel">
                                    Cooldown
                                  </p>
                                  <p className="fpm-cooldownText">
                                    {session.cooldown.join(' • ')}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="fpm-dayFallbackText">
                              {daySchedule?.type} - {daySchedule?.focus?.replace('_', ' ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Progression Notes */}
                  {selectedProgramDetails.program?.generation_context?.ai_response?.progression_notes && (
                    <div className="fpm-progressionNote">
                      <p className="fpm-progressionText">
                        <strong>Progression:</strong> {selectedProgramDetails.program.generation_context.ai_response.progression_notes}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Periodization Info */}
              {selectedProgramDetails.periodization && (
                <div className="fpm-periodizationSection">
                  <h4 className="fpm-periodizationTitle">
                    Current Training Phase
                  </h4>
                  <p className="fpm-periodizationPhase">
                    <strong>{selectedProgramDetails.periodization.mesocycle_phase}</strong> -
                    Week {selectedProgramDetails.periodization.mesocycle_number}
                  </p>
                  <p className="fpm-periodizationFocus">
                    {selectedProgramDetails.periodization.mesocycle_focus}
                  </p>
                </div>
              )}

              {/* Progress Records */}
              {selectedProgramDetails.progress_records && selectedProgramDetails.progress_records.length > 0 && (
                <div>
                  <h4 className="fpm-progressTitle">
                    Recent Progress
                  </h4>
                  <div className="fpm-progressList">
                    {selectedProgramDetails.progress_records.slice(0, 3).map((record, i) => (
                      <div
                        key={i}
                        className="fpm-progressRow"
                      >
                        <span className="fpm-progressWeek">Week {record.week_number}</span>
                        <span className="fpm-progressSessions">
                          Completed: {record.sessions_completed || 0} sessions
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal - EXACT COPY FROM MEAL PLAN */}
      {showFileViewer && fileViewingProgram?.attached_file_path && (
        <div className="fpm-fileViewerOverlay">
          <div className="fpm-fileViewerModal">
            {/* Header */}
            <div className="fpm-fileViewerHeader">
              <div>
                <h3 className="fpm-fileViewerTitle">
                  {fileViewingProgram.upload_filename}
                </h3>
                <p className="fpm-fileViewerSize">
                  {(fileViewingProgram.attached_file_size / 1024).toFixed(1)} KB
                </p>
              </div>
              <button
                onClick={() => closeFileViewer()}
                className="fpm-fileViewerCloseBtn"
                onMouseOver={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* File Content */}
            <div className="fpm-fileViewerBody" style={fileContent.startsWith('__PDF__') ? { padding: 0 } : {}}>
              {fileContent.startsWith('__PDF__') ? (
                <iframe
                  src={fileContent.replace('__PDF__', '')}
                  style={{ width: '100%', minHeight: '70vh', border: 'none', display: 'block' }}
                  title="PDF Viewer"
                />
              ) : (
                <pre className="fpm-fileViewerPre">
                  {fileContent}
                </pre>
              )}
            </div>

            {/* Footer with Print and Dismiss Buttons */}
            <div className="fpm-fileViewerFooter">
              {fileContent.startsWith('__PDF__') ? (
                <span className="fpm-fileViewerHint">Use the PDF toolbar to Print</span>
              ) : (
                <button
                  onClick={() => printFileContent(fileContent, fileViewingProgram?.upload_filename)}
                  className="fpm-fileViewerPrintBtn"
                >
                  Print
                </button>
              )}
              <button
                onClick={() => closeFileViewer()}
                className="fpm-fileViewerDismissBtn"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Exercise Video Modal */}
      {selectedVideoExercise && (
        <div
          className="fpm-videoOverlay"
          onClick={() => setSelectedVideoExercise(null)}
        >
          <div
            className="fpm-videoModal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="fpm-videoHeader">
              <div>
                <h3 className="fpm-videoTitle">
                  {selectedVideoExercise.exercise || selectedVideoExercise.exercise_name}
                </h3>
                <p className="fpm-videoMeta">
                  {selectedVideoExercise.category} • {selectedVideoExercise.primary_muscles ?
                    (Array.isArray(selectedVideoExercise.primary_muscles)
                      ? selectedVideoExercise.primary_muscles.map(m => typeof m === 'string' ? m : m.name).join(', ')
                      : selectedVideoExercise.primary_muscles)
                    : 'Multiple muscles'}
                </p>
              </div>
              <button
                onClick={() => setSelectedVideoExercise(null)}
                className="fpm-videoCloseBtn"
              >
                ×
              </button>
            </div>

            {/* Video Player */}
            <div className="fpm-videoPlayerSection">
              <video
                src={selectedVideoExercise.video_url}
                controls
                autoPlay
                className="fpm-videoPlayer"
              />
            </div>

            {/* Tips Section */}
            {selectedVideoExercise.tips && (
              <div className="fpm-videoTipsSection">
                <h4 className="fpm-videoTipsTitle">
                  Tips
                </h4>
                <p className="fpm-videoTipsText">
                  {selectedVideoExercise.tips}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FitnessProgramManagement;